import Vue from 'vue';
import VueRouter from 'vue-router';
import grayInit, { setTidInHeaders } from '@/app-shared/gray-init';
import { wxAuthCheck } from '@/app-shared/utils/wechat';
import { setPage } from '../page-config/index';
import initCheckAuth from '../mixins/chechAuth.js';
import {
  isWechat,
  getQueryString,
  setHclGlobalConfig,
  addParamsToUrl,
  removeUrlParameter
} from '@/app-shared/utils/tool.js';
import { setGlobalCustomOptions } from '@vhcl/core';
import {
  setEnvConfig,
  setRequestHeaders,
  ssoLogin,
  syncBothStorage,
  syncToken,
  syncVisitorId,
  syncVisitorIdToSSO,
  saveAndSyncBothStorage,
  loginOut
} from '@vhcl/saas-login';

import tidCenter from '@/app-shared/utils/tidCenter.js';

// 控制台报错：Avoided redundant navigation to current location: “/xxxxxx“解决方法
// 获取原型对象push函数
const originalPush = VueRouter.prototype.push;
// 获取原型对象replace函数
const originalReplace = VueRouter.prototype.replace;
// 修改原型对象中的push函数
VueRouter.prototype.push = function push(location) {
  return originalPush.call(this, location).catch(err => err);
};
// 修改原型对象中的replace函数
VueRouter.prototype.replace = function replace(location) {
  return originalReplace.call(this, location).catch(err => err);
};

Vue.use(VueRouter);

const routes = [
  {
    path: '/lives/watch/:id',
    name: 'LiveRoom',
    component: () => import(/* webpackChunkName: "LiveRoom" */ '../views/Main.vue'),
    meta: { title: '直播间', grayType: 'webinar' }
  },
  {
    path: '/lives/embedclient/watch/:id', //嵌入观看页
    name: 'LiveEmbedRoom',
    component: () => import(/* webpackChunkName: "LiveEmbedRoom" */ '../views/Main.vue'),
    meta: { title: '直播间嵌入', grayType: 'webinar' }
  },
  {
    path: '/lives/embedclientfull/watch/:id', //完全嵌入观看页
    name: 'LiveEmbedFullRoom',
    component: () => import(/* webpackChunkName: "LiveEmbedFullRoom" */ '../views/Main.vue'),
    meta: { title: '直播间嵌入', grayType: 'webinar' }
  },
  {
    path: '/lives/embedclientvideo/watch/:id', //单视频嵌入观看页
    name: 'LiveEmbedVideoRoom',
    component: () => import(/* webpackChunkName: "LiveEmbedVideoRoom" */ '../views/Main.vue'),
    meta: { title: '直播间嵌入', grayType: 'webinar' }
  },
  {
    path: '/lives/subscribe/:id',
    name: 'Subscribe',
    component: () => import(/* webpackChunkName: "Subscribe" */ '../views/Main.vue'),
    meta: { title: '预约', grayType: 'webinar' }
  },
  {
    path: '/lives/embedclient/subscribe/:id', //嵌入预约页
    name: 'SubscribeEmbed',
    component: () => import(/* webpackChunkName: "SubscribeEmbed" */ '../views/Main.vue'),
    meta: { title: '预约嵌入', grayType: 'webinar' }
  },
  {
    path: '/lives/entryform/:id',
    name: 'entryForm',
    component: () => import(/* webpackChunkName: "entryForm" */ '../views/Subscribe/entryForm.vue'),
    meta: { title: '独立报名表单', grayType: 'webinar' }
  },
  {
    path: '/special/entryform/:id',
    component: () => import(/* webpackChunkName: "entryForm" */ '../views/Subscribe/entryForm.vue'),
    name: 'entryFormSubject',
    meta: { title: '独立报名表单', grayType: 'subject' }
  },
  {
    path: '/forgetPwd/:id',
    name: 'forgetPwd',
    component: () => import(/* webpackChunkName: "forgetPwd" */ '../views/forgetPwd/index.vue'),
    meta: { title: '忘记密码', grayType: 'user' }
  },
  {
    path: '/special/detail',
    component: () => import(/* webpackChunkName: "subject" */ '../views/Subject/index.vue'),
    name: 'Subject',
    meta: { title: '专题详情', page: 'subject', grayType: 'subject' }
  },
  // 错误页
  {
    path: '/lives/watchError/:id/:code', // 统一错误页
    name: 'PageError',
    component: () => import(/* webpackChunkName: "PageError" */ '../views/ErrorPage/error.vue'),
    meta: { title: '系统异常' }
  },
  {
    // 其它没有匹配到的路由都会跳至此模块(404）
    // 该路由为必须路由，不需要权限，必须放在最后
    path: '*',
    name: 'notfound',
    redirect: '/lives/watchError/404/404'
  }
];

const router = new VueRouter({
  mode: 'history',
  base: process.env.ROUTER_BASE_URL,
  routes
});

// 当前是否为开发环境
const isDev = process.env.NODE_ENV === 'development';

router.beforeEach(async (to, from, next) => {
  if (to.meta.page && (!window.$serverConfig || window.$serverConfig._page !== to.meta.page)) {
    // 根据不同的页面，动态加载不同的配置
    await setPage(to.meta.page);
  }

  const res = to.name != 'PageError' && (await grayInit(to));
  if (res?.code == 5003 && to.name != 'PageError') {
    next({
      name: 'PageError',
      params: {
        id: to.params.id,
        code: res.code
      },
      query: {
        sourceUrl: encodeURI(window.location.href)
      }
    });
    return;
  }
  setGlobalCustomOptions({
    biz_id: process.env.VUE_APP_BIZ_ID || 2,
    app_id: res?.data?.paas_app_id
  });
  // 设置ssoLogin API的环境
  setEnvConfig({
    env: process.env.VUE_APP_ENV_HCL,
    request: {
      headers: {
        'gray-id': sessionStorage.getItem('initGrayId') || ''
      }
    },
    client: 'pc',
    expires: res?.data?.permission?.permission_value
      ? Number(JSON.parse(res.data.permission.permission_value)['watch.cache_time'])
      : 7
  });

  const grayId = sessionStorage.getItem('initGrayId') || '';
  setRequestHeaders({
    'gray-id': grayId
  });
  setHclGlobalConfig({
    platform: 7,
    grayId,
    isHomologous: process.env.VUE_APP_HOMOLOGOUS
  });

  /**
   * 嘉实多 标品与单视频嵌入打通报名状态(tid)
   * @type {string}
   */
  const tokenId = getQueryString('token_id');
  const webinarId = to?.params?.id;
  if (tokenId && webinarId) {
    await tidCenter.setTid(tokenId, webinarId, 2, '');
  }

  // 官网嵌入webcasting场景：
  // 外部官网退出后，会拼接logged_out=1,代表外部触发退出动作，
  // 通知嵌入页清空登录(token)、报名(tokenId)、生成新的visitorId
  const loggedOut = getQueryString('logged_out');
  if (loggedOut + '' === '1') {
    loginOut().then(res => {
      if (res.code === 200) {
        window.localStorage.clear();
        window.sessionStorage.clear();
        let newQuery = {
          ...to.query
        };
        delete newQuery.logged_out;
        let url = router.resolve({ path: to.path, query: newQuery }).href;
        window.location.replace(url);
        return true;
      }
    });
  }

  // 地址栏 token、visitorId、openId优先级最高
  const openId = getQueryString('openId') || getQueryString('open_id');
  const visitorId = getQueryString('visitorId');
  const token = getQueryString('token') || res?.data?.ticket?.token;

  visitorId && (await syncVisitorIdToSSO(grayId, visitorId));
  token && (await saveAndSyncBothStorage('token', token));
  const visitor_id = sessionStorage.getItem('visitorId');
  window.bonreeJsBridge && window.bonreeJsBridge.setUserID(visitor_id);
  const depWxAppid = getQueryString('dep_wx_appid');
  if (openId) {
    if (depWxAppid) {
      sessionStorage.setItem('dep_wx_appid', depWxAppid);
    } /* else {
      sessionStorage.setItem('dep_wx_appid', 'third');
    }*/
    await saveAndSyncBothStorage('open_id', openId);
  }

  console.log('---grayInit---', res);
  console.log('initGrayId:', window.sessionStorage.getItem('initGrayId'));

  if (to.name !== 'forgetPwd') {
    // 忘记密码页面不需要下面的处理

    if (!isDev) {
      try {
        if (!JSON.parse(window.sessionStorage.getItem('isPrivateConsumer'))) {
          // 不是B下C才走sso登录
          console.log('---ssoLogin---');
          await ssoLogin(); // sso自动登录置换token
        }
      } catch (ex) {
        console.warn('ssoLogin ex:', ex);
      }
    }
    // A下C、B下C同步相关数据(visitorId,token,openId)
    await syncVisitorId(sessionStorage.getItem('initGrayId'));
    !token && (await syncToken());
    let arr = [];
    isWechat() && arr.push(syncBothStorage('open_id'));
    await Promise.all(arr);
    console.log('同步的token:', window.sessionStorage.getItem('token'));
    // 设置token_id到请求头
    await setTidInHeaders(to);

    // 专题页未登录进行三方登录时
    if (to.name === 'Subject' && !sessionStorage.getItem('token')) {
      initCheckAuth.methods.initCheckAuth.call({ $route: to });
    }
  }
  if (res) {
    //处理限流逻辑
    if (res.code == 200) {
      await wxAuthCheck(to, next);
    } else {
      next({
        name: 'PageError',
        params: {
          id: to.params.id,
          code: res.code
        }
      });
    }
  } else {
    await wxAuthCheck(to, next);
  }
  const url = removeUrlParameter(window.location.href, 'ticket');

  if (to.query.ticket) {
    window.location.replace(url);
  }
});

export default router;
